import type { Auftrag, BildEntscheidung, EntscheidungsPayload, TextAntwort } from '@awf/shared';
import { MAX_KORREKTURRUNDEN, offeneRunde } from '@awf/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { KiHinweis } from '../ui/ki-hinweis.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { EmptyState, ErrorState, LoadingState } from '../ui/states.js';
import { Statusmarke, TON_JE_GRUPPE } from '../ui/statusmarke.js';
import { BildBewertung, bildName } from './bild-bewertung.js';
import { BildLupeProvider } from './bild-lupe.js';
import stile from './bilder-freigabe-page.module.css';
import seite from './freigabe-page.module.css';
import spalte from './rueckmelde-spalte.module.css';
import type { AuftragsQuelle } from './runs-source.js';
import { gruppeVon, STATUS_TEXT } from './status.js';
import { rundeBeansprucht, schichtArtAusFirestore, type Absendung } from './uebergabe.js';
import { UebergabeSchicht } from './uebergabe-schicht.js';
import { useAuftrag } from './use-runs.js';

export interface BilderFreigabeProps {
  quelle: AuftragsQuelle;
  holen: (runId: string) => Promise<TextAntwort>;
  senden: (runId: string, payload: EntscheidungsPayload) => Promise<void>;
}

const BILD_VALIDIERUNG = 'Bitte beschreiben Sie, was am Bild geändert werden soll.';
const KEINE_BILDER_TEXT = 'Zu diesem Auftrag sind keine Bilder zurückgekommen.';

/**
 * Freigabe eines Bilder-Auftrags (Entscheidung 2026-10-13: im Frontend statt per Mail-Formular).
 * Dieselbe Seite wie beim Ratgeber, nur ohne Text: je Bild Lupe, "Passt" oder "Ändern" mit
 * Rückmeldung. Bei genau einem Bild gibt "Passt" direkt frei, "Ändern" sendet ueber den Knopf unter
 * dem Feld; bei mehreren folgt "Freigeben" bzw. "Zur Überarbeitung senden" als Sammelknopf.
 *
 * Die Sperre folgt derselben Regel wie `FreigabePage` (Spec 2026-09-29-absende-rennen, 5.1):
 * gesperrt, solange Firestore keine offene Runde zeigt (`offeneRunde`) oder dieser Tab zur
 * offenen Runde schon abgeschickt hat. Darueber steht die `UebergabeSchicht`. Version, Bildname,
 * Ordner, Format und Historien schickt die Seite nicht mit: n8n liest sie selbst ueber die run_id.
 */
/**
 * "Passt" / "Ändern" je Bild in der Rueckmeldespalte, dauerhaft sichtbar (Rueckmeldung
 * 2026-10-13: nicht auf dem Bild und nicht erst beim Ueberfahren). "Ändern" oeffnet das Feld
 * direkt darunter und setzt den Fokus hinein, "Passt" leert es, damit kein liegen gebliebener
 * Text unsichtbar mitgeht.
 *
 * Mit `onFreigeben` (genau ein Bild) ist "Passt" selbst die Freigabe und "Ändern" bekommt den
 * Knopf zum Absenden unter dem Feld: Ein eigenes "Freigeben" darunter wiederholte nur "Passt"
 * (Rueckmeldung 2026-10-16). Bei mehreren Bildern bleibt "Passt" eine Auswahl je Bild.
 */
function BildEntscheidungsTeil({
  name,
  mitName,
  entscheidung,
  rundenlimitErreicht,
  deaktiviert,
  aendern,
  onFreigeben,
}: {
  name: string;
  mitName: boolean;
  entscheidung: BildEntscheidung;
  rundenlimitErreicht: boolean;
  deaktiviert: boolean;
  aendern: (aenderung: Partial<BildEntscheidung>) => void;
  onFreigeben?: (() => void) | undefined;
}) {
  const feld = useRef<HTMLTextAreaElement>(null);
  const fokus = useRef(false);
  const wirdGeaendert = entscheidung.aktion === 'optimize';
  useEffect(() => {
    if (fokus.current && wirdGeaendert) {
      fokus.current = false;
      feld.current?.focus();
    }
  }, [wirdGeaendert]);
  return (
    <div className={spalte.teil}>
      {mitName && <p className={spalte.kommentar}>{name}</p>}
      <div className={stile.entscheidung} role="group" aria-label={`Bewertung ${name}`}>
        {onFreigeben ? (
          <button
            type="button"
            // Gefuellt nur, solange nicht geaendert wird: sonst stuenden zwei gefuellte Knoepfe
            // nebeneinander, und keiner zeigte die Wahl.
            className={`eds-button ${wirdGeaendert ? 'eds-button--secondary' : 'eds-button--primary'} eds-button--sm`}
            disabled={deaktiviert}
            onClick={() => {
              aendern({ aktion: 'approve', feedback: '' });
              onFreigeben();
            }}
          >
            <LinienSymbol name="check" />
            Passt
          </button>
        ) : (
          <button
            type="button"
            className="eds-button eds-button--secondary eds-button--sm"
            aria-pressed={!wirdGeaendert}
            disabled={deaktiviert}
            onClick={() => aendern({ aktion: 'approve', feedback: '' })}
          >
            <LinienSymbol name="check" />
            Passt
          </button>
        )}
        {!rundenlimitErreicht && (
          <button
            type="button"
            className="eds-button eds-button--secondary eds-button--sm"
            aria-pressed={wirdGeaendert}
            disabled={deaktiviert}
            onClick={() => {
              fokus.current = true;
              aendern({ aktion: 'optimize' });
              if (wirdGeaendert) feld.current?.focus();
            }}
          >
            <LinienSymbol name="message" />
            Ändern
          </button>
        )}
      </div>
      {wirdGeaendert && (
        <>
          <label className={spalte.leise} htmlFor={`bild-feedback-${entscheidung.index}`}>
            Was soll anders werden?
          </label>
          <textarea
            id={`bild-feedback-${entscheidung.index}`}
            ref={feld}
            className={`eds-field__input ${spalte.eingabe}`}
            aria-label={`Rückmeldung zu ${name}`}
            value={entscheidung.feedback}
            disabled={deaktiviert}
            onChange={(e) => aendern({ feedback: e.target.value })}
          />
          {onFreigeben && (
            <button type="submit" className="eds-button eds-button--primary" disabled={deaktiviert}>
              Zur Überarbeitung senden
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function BilderFreigabePage({ quelle, holen, senden }: BilderFreigabeProps) {
  const { runId = '' } = useParams();
  const zustand = useAuftrag(quelle, runId);
  const offen = zustand.art === 'daten' && zustand.auftrag ? offeneRunde(zustand.auftrag) : null;
  const navigate = useNavigate();

  const [absendung, setAbsendung] = useState<Absendung | null>(null);
  const absendungRef = useRef<Absendung | null>(null);
  function setzeAbsendung(neu: Absendung | null) {
    absendungRef.current = neu;
    setAbsendung(neu);
  }
  const eigeneAbsendung = absendung !== null && absendung.runId === runId ? absendung : null;
  // Wie in `FreigabePage`: `runde_veraltet`, waehrend Firestore die Runde noch offen zeigt.
  const [veraltet, setVeraltet] = useState<{ runId: string; runde: number } | null>(null);
  // Das eigene Absenden ist gescheitert, Firestore zeigt aber noch den eigenen Anspruch.
  const [fehlgeschlagen, setFehlgeschlagen] = useState<{ runId: string; runde: number } | null>(
    null,
  );
  const gesperrt =
    offen === null ||
    (eigeneAbsendung !== null && eigeneAbsendung.runde === offen) ||
    (veraltet !== null && veraltet.runId === runId && veraltet.runde === offen);
  const offenRef = useRef(offen);
  offenRef.current = offen;
  const aktuelleRunId = useRef(runId);
  aktuelleRunId.current = runId;
  const aktiv = useRef(false);
  useEffect(() => {
    aktiv.current = true;
    return () => {
      aktiv.current = false;
    };
  }, []);

  const [antwort, setAntwort] = useState<TextAntwort | null>(null);
  const [geladenFuer, setGeladenFuer] = useState<{ runId: string; runde: number } | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [wiederholVersuch, setWiederholVersuch] = useState(0);
  const [entscheidungen, setEntscheidungen] = useState<BildEntscheidung[]>([]);
  const [bildValidierung, setBildValidierung] = useState<string | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const effektRunId = useRef<string | null>(null);

  // Ohne offene Runde (Uebergabe laeuft) gelten die zuletzt geladenen Bilder weiter: Sie stehen
  // ausgegraut unter der Schicht.
  const antwortAktuell =
    geladenFuer !== null &&
    geladenFuer.runId === runId &&
    (offen === null || geladenFuer.runde === offen);

  useEffect(() => {
    const laufGewechselt = effektRunId.current !== runId;
    effektRunId.current = runId;
    setVeraltet((v) =>
      v !== null && (laufGewechselt || v.runId !== runId || v.runde !== offen) ? null : v,
    );
    if (offen === null && !laufGewechselt) return;
    setFehlgeschlagen(null);
    // Schon fuer genau diese Runde geladen (offen N -> null -> N nach `gibRundeFrei`): Neu laden
    // verwuerfe die Eingaben, die nach einem gescheiterten Absenden erhalten bleiben muessen.
    if (
      offen !== null &&
      geladenFuer !== null &&
      geladenFuer.runId === runId &&
      geladenFuer.runde === offen
    ) {
      return;
    }
    setzeAbsendung(null);
    setAntwort(null);
    setGeladenFuer(null);
    setLadeFehler(null);
    setBildValidierung(null);
    setSendeFehler(null);
    if (offen === null) return;

    let gilt = true;
    holen(runId).then(
      (geladen) => {
        if (!gilt) return;
        // Eine Freigabe ohne Bild saehe aus wie ein Ergebnis -- sichtbar scheitern statt leer.
        if (geladen.bilder.length === 0) {
          setLadeFehler(KEINE_BILDER_TEXT);
          return;
        }
        setAntwort(geladen);
        setGeladenFuer({ runId, runde: offen });
        setEntscheidungen(
          geladen.bilder.map((b) => ({ index: b.index, aktion: 'approve', feedback: '' })),
        );
      },
      (fehler: unknown) => {
        if (!gilt) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.text : ALLGEMEINER_FEHLERTEXT);
      },
    );
    return () => {
      gilt = false;
    };
    // `geladenFuer` bewusst nicht: Der Effekt soll nicht nach dem eigenen Laden erneut laufen.
  }, [holen, runId, offen, wiederholVersuch]);

  if (zustand.art === 'laedt') return <LoadingState text="Auftrag wird geladen …" />;
  if (zustand.art === 'fehler') {
    return <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />;
  }
  const auftrag = zustand.auftrag;
  if (!auftrag) {
    return (
      <EmptyState
        titel="Dieser Auftrag existiert nicht oder gehört zu einem anderen Konto."
        text="Prüfen Sie den Verweis, über den Sie hierher gekommen sind."
      >
        <Link className="eds-button eds-button--primary" to="/">
          Zur Auftragsliste
        </Link>
      </EmptyState>
    );
  }

  const art = eigeneAbsendung?.art ?? (offen === null ? schichtArtAusFirestore(auftrag) : null);
  const eigenerFehlschlag =
    fehlgeschlagen !== null &&
    fehlgeschlagen.runId === runId &&
    fehlgeschlagen.runde === (auftrag.runde ?? 1) &&
    rundeBeansprucht(auftrag);
  const zeigeSchicht =
    gesperrt && art !== null && auftrag.status !== 'fertig' && !eigenerFehlschlag;

  if (!zeigeSchicht && auftrag.status !== 'wartet_auf_freigabe') {
    return (
      <EmptyState
        titel="Dieser Auftrag wartet nicht auf eine Freigabe."
        text={`Aktueller Status: ${STATUS_TEXT[auftrag.status]}.`}
      >
        <Link className="eds-button eds-button--primary" to={`/auftraege/${runId}`}>
          Zum Auftrag
        </Link>
      </EmptyState>
    );
  }

  const runde = auftrag.runde ?? 1;
  const rundenlimitErreicht = runde > MAX_KORREKTURRUNDEN;
  const bilder = antwort?.bilder ?? [];
  const zurUeberarbeitung = entscheidungen.some((e) => e.aktion === 'optimize');
  const knopfText = zurUeberarbeitung ? 'Zur Überarbeitung senden' : 'Freigeben';
  // Genau ein Bild: "Passt" gibt frei, kein Sammelknopf (siehe `BildEntscheidungsTeil`).
  const direkt = bilder.length === 1;
  const meldungen = (sendeFehler || bildValidierung) && (
    <>
      {sendeFehler && (
        <div className="eds-alert eds-alert--error" role="alert">
          <p>{sendeFehler}</p>
        </div>
      )}
      {bildValidierung && (
        <p className="eds-field__hint" role="alert">
          {bildValidierung}
        </p>
      )}
    </>
  );

  function aendern(index: number, aenderung: Partial<BildEntscheidung>) {
    setBildValidierung(null);
    setEntscheidungen((alle) => alle.map((e) => (e.index === index ? { ...e, ...aenderung } : e)));
  }

  /**
   * `liste` fuer "Passt" bei genau einem Bild: Der Klick setzt die Entscheidung und sendet im
   * selben Takt, der Zustand traegt sie da noch nicht.
   */
  async function absenden(liste: BildEntscheidung[] = entscheidungen) {
    if (gesperrt || !antwort || !antwortAktuell) return;
    const zurUeberarbeitung = liste.some((e) => e.aktion === 'optimize');
    // Zwei Ausloeser im selben Takt sehen beide noch den Render vor `setzeAbsendung`; die Ref
    // haelt den zweiten auf (wie in `FreigabePage`).
    if (absendungRef.current?.runId === runId && absendungRef.current.runde === runde) return;
    if (liste.some((e) => e.aktion === 'optimize' && e.feedback.trim() === '')) {
      setBildValidierung(BILD_VALIDIERUNG);
      return;
    }
    const meine: Absendung = {
      runId,
      runde,
      art: zurUeberarbeitung ? 'ueberarbeitung' : 'freigabe',
    };
    setFehlgeschlagen(null);
    setzeAbsendung(meine);
    setSendeFehler(null);
    try {
      await senden(runId, {
        runId,
        runde,
        // Die Bilder-Kette liest `bilder[0]` vor dem kettenweiten Feld (Praezedenz in
        // packages/shared, `EntscheidungsPayload.bilder`). Das kettenweite Feld spiegelt sie,
        // damit auch ein Adapter ohne diese Regel dasselbe liest.
        aktion: zurUeberarbeitung ? 'optimize' : 'approve',
        gesamtFeedback: '',
        anmerkungen: [],
        bilder: liste.map((e) => ({
          ...e,
          feedback: e.aktion === 'optimize' ? e.feedback.trim() : '',
        })),
      });
      if (!aktiv.current || aktuelleRunId.current !== runId || absendungRef.current !== meine) {
        return;
      }
      if (meine.art === 'freigabe') {
        navigate(`/auftraege/${runId}`, {
          replace: true,
          state: { bestaetigung: 'Freigegeben. Die Bilder werden jetzt fertiggestellt.' },
        });
      }
    } catch (fehler) {
      const nochMeine = absendungRef.current === meine;
      if (nochMeine) setzeAbsendung(null);
      if (!aktiv.current || aktuelleRunId.current !== runId || !nochMeine) return;
      const istVeraltet = fehler instanceof ApiError && fehler.code === 'runde_veraltet';
      if (!istVeraltet && offenRef.current === null) setFehlgeschlagen({ runId, runde });
      if (istVeraltet && offenRef.current === runde) setVeraltet({ runId, runde });
      setSendeFehler(fehler instanceof ApiError ? fehler.text : ALLGEMEINER_FEHLERTEXT);
    }
  }

  return (
    <div className={seite.seite}>
      <KopfDerSeite auftrag={auftrag} runId={runId} />

      {ladeFehler && (
        <ErrorState text={ladeFehler} onWiederholen={() => setWiederholVersuch((n) => n + 1)} />
      )}

      {!ladeFehler && !(antwort && antwortAktuell) && !zeigeSchicht && (
        <LoadingState text="Die Bilder werden geladen …" />
      )}

      {!ladeFehler && antwort && antwortAktuell && (
        <div className={seite.schichtRahmen}>
          <BildLupeProvider>
            <form
              className={seite.arbeitsflaeche}
              inert={zeigeSchicht}
              onSubmit={(e) => {
                e.preventDefault();
                void absenden();
              }}
              noValidate
            >
              <div className={stile.bilderspalte}>
                <p className={seite.arbeitsHinweis}>
                  <LinienSymbol name="message" groesse={14} />
                  <span>
                    {bilder.length === 1
                      ? 'Bewerten Sie das Bild rechts mit „Passt“ oder „Ändern“. Über das Zeichen oben rechts im Bild sehen Sie es groß.'
                      : 'Bewerten Sie jedes Bild rechts mit „Passt“ oder „Ändern“. Über das Zeichen oben rechts im Bild sehen Sie es groß.'}
                  </span>
                </p>
                <div className={stile.bilder}>
                  {bilder.map((bild, i) => (
                    <BildBewertung
                      key={bild.index}
                      bild={bild}
                      name={bildName(bild, i)}
                      entscheidung={
                        entscheidungen.find((e) => e.index === bild.index) ?? {
                          index: bild.index,
                          aktion: 'approve',
                          feedback: '',
                        }
                      }
                      rundenlimitErreicht={rundenlimitErreicht}
                      deaktiviert={gesperrt}
                      variante={bilder.length === 1 ? 'hero' : 'galerie'}
                      aendern={(aenderung) => aendern(bild.index, aenderung)}
                      bewertungAussen
                    />
                  ))}
                </div>
              </div>
              <div className={seite.spalte}>
                <section className={spalte.spalte} aria-labelledby="bilder-rueckmeldung">
                  <div className={spalte.teil}>
                    <h2 id="bilder-rueckmeldung" className={spalte.titel}>
                      Ihre Rückmeldung
                    </h2>
                  </div>
                  {bilder.map((bild, i) => (
                    <BildEntscheidungsTeil
                      key={bild.index}
                      name={bildName(bild, i)}
                      mitName={bilder.length > 1}
                      entscheidung={
                        entscheidungen.find((e) => e.index === bild.index) ?? {
                          index: bild.index,
                          aktion: 'approve',
                          feedback: '',
                        }
                      }
                      rundenlimitErreicht={rundenlimitErreicht}
                      deaktiviert={gesperrt}
                      aendern={(aenderung) => aendern(bild.index, aenderung)}
                      onFreigeben={
                        direkt
                          ? () =>
                              void absenden([
                                { index: bild.index, aktion: 'approve', feedback: '' },
                              ])
                          : undefined
                      }
                    />
                  ))}
                  {rundenlimitErreicht && (
                    <div className={spalte.teil}>
                      <p className={spalte.leise}>
                        Nach {MAX_KORREKTURRUNDEN} Korrekturrunden ist nur noch die Freigabe
                        möglich.
                      </p>
                    </div>
                  )}
                  {direkt ? (
                    meldungen && <div className={spalte.teil}>{meldungen}</div>
                  ) : (
                    <div className={spalte.abschluss}>
                      {meldungen}
                      <button
                        type="submit"
                        className="eds-button eds-button--primary"
                        disabled={gesperrt}
                      >
                        {knopfText}
                      </button>
                    </div>
                  )}
                </section>
                <KiHinweis />
              </div>
            </form>
          </BildLupeProvider>
          {zeigeSchicht && art !== null && (
            <UebergabeSchicht
              art={art}
              status={auftrag.status}
              runId={runId}
              eigene={eigeneAbsendung !== null}
            />
          )}
        </div>
      )}

      {/* Die Schicht auch ohne geladene Bilder, etwa beim Wiederkommen waehrend der Ueberarbeitung. */}
      {zeigeSchicht && art !== null && !(antwort && antwortAktuell) && (
        <div className={seite.schichtRahmen}>
          <UebergabeSchicht
            art={art}
            status={auftrag.status}
            runId={runId}
            eigene={eigeneAbsendung !== null}
          />
        </div>
      )}
    </div>
  );
}

function KopfDerSeite({ auftrag, runId }: { auftrag: Auftrag; runId: string }) {
  return (
    <Seitenkopf
      titel={auftrag.thema}
      pfad={[
        { text: 'Review', ziel: `/?typ=${auftrag.typ}&gruppe=wartet` },
        { text: 'Fortschritt Ihres Auftrags', ziel: `/auftraege/${encodeURIComponent(runId)}` },
      ]}
      beschreibung={<>Freigaberunde {auftrag.runde ?? 1}</>}
      aktion={
        <Statusmarke ton={TON_JE_GRUPPE[gruppeVon(auftrag.status)]}>
          {STATUS_TEXT[auftrag.status]}
        </Statusmarke>
      }
    />
  );
}
